import type { FloorPlanSheetView } from '@obraplan/core/application/use-cases/takeoff/floor-plan-sheet-use-cases';
import type { UnitPosition } from '@obraplan/core/domain/takeoff/floor-plan';
import type { FloorPlanSheetSource } from '@obraplan/core/domain/takeoff/floor-plan-sheet';
import type { UnitTypeId } from '@obraplan/core/domain/shared/ids';
import type { WorkspaceDraft } from './typology-draft';

/**
 * As contas pequenas da prancha no servidor (Fase 24), fora do hook para serem testadas
 * sem montar tela nenhuma.
 */

/**
 * Em que desenho de pavimento a tipologia está — é dele a prancha que ela usa.
 *
 * A primeira posição, como `pavimentoPorTipologia` já faz: a mesma tipologia em dois
 * desenhos é caso que o quantitativo ainda não distingue, e escolher aqui outro critério
 * faria a prancha e o tipo de pavimento discordarem sobre onde ela mora.
 */
export function desenhoDaTipologia(
  positions: readonly UnitPosition[],
  unitTypeId: UnitTypeId,
): string | undefined {
  return positions.find((position) => position.unitTypeId === unitTypeId)?.floorPlanId;
}

/** As tipologias traçadas na folha de um desenho — as que a recalibragem reescala. */
export function tipologiasDoDesenho(
  positions: readonly UnitPosition[],
  floorPlanId: string,
): readonly UnitTypeId[] {
  return [
    ...new Set(
      positions
        .filter((position) => position.floorPlanId === floorPlanId)
        .map((position) => position.unitTypeId),
    ),
  ];
}

/**
 * Para que desenho sobe a prancha que o navegador guardava antes da Fase 24.
 *
 * A de antes era **uma para a bancada inteira** — e a bancada nem era da obra: o
 * `localStorage` é do navegador, e a mesma prancha aparecia em qualquer obra aberta nele.
 * O que diz de que desenho ela era é o traçado: ambiente com contorno foi desenhado sobre
 * ela. Então vai para o desenho com mais ambientes traçados **nesta obra**.
 *
 * Obra sem traçado nenhum não recebe prancha nenhuma: ela pode ser de outra obra, e
 * pendurá-la aqui seria pior do que esperar. Ela continua guardada até a obra certa abrir.
 */
export function alvoDaPranchaAntiga(
  workspace: WorkspaceDraft,
  positions: readonly UnitPosition[],
): string | undefined {
  const tracadosPorDesenho = new Map<string, number>();
  for (const typology of workspace.typologies) {
    const tracados = typology.rooms.filter((room) => room.outline !== undefined).length;
    if (tracados === 0) continue;
    const desenho = desenhoDaTipologia(positions, typology.unitTypeId);
    if (desenho === undefined) continue;
    tracadosPorDesenho.set(desenho, (tracadosPorDesenho.get(desenho) ?? 0) + tracados);
  }

  let alvo: string | undefined;
  let mais = 0;
  for (const [desenho, tracados] of tracadosPorDesenho) {
    if (tracados > mais) {
      alvo = desenho;
      mais = tracados;
    }
  }
  return alvo;
}

/**
 * `"29/09/2026"` — o dia em que a prancha foi anexada.
 *
 * No fuso de São Paulo, e não no do aparelho: a obra é aqui, e um anexo das 22h não pode
 * aparecer com a data do dia seguinte só porque alguém abriu a tela com o relógio em UTC.
 */
export function dataDaPrancha(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

/**
 * O aviso de remover e de trocar (RN-246): *"Anexada por X em dd/mm/aaaa. Deseja remover?"*
 *
 * É a frase do pedido, e ela existe porque a prancha deixou de ser de quem a carregou: a
 * que está na tela pode ser a que o engenheiro da obra pôs lá ontem, e tirá-la sem saber
 * disso é desfazer o trabalho de outra pessoa.
 */
export function avisoDeQuemAnexou(
  anexada: Pick<FloorPlanSheetView, 'attachedByName' | 'attachedAt'>,
  gesto: 'remover' | 'trocar',
): string {
  return `Anexada por ${anexada.attachedByName} em ${dataDaPrancha(anexada.attachedAt)}. Deseja ${gesto}?`;
}

/**
 * O que a tela diz da origem do arquivo (RN-248) — vazio quando é o original.
 *
 * As outras duas precisam ser ditas: quem vai trocar a prancha, ou ampliar até o fim e
 * estranhar a nitidez, precisa saber que aquilo não é o PDF.
 */
export function origemDaPrancha(source: FloorPlanSheetSource): string | undefined {
  switch (source) {
    case 'ORIGINAL':
      return undefined;
    case 'RENDERIZADA':
      return 'página renderizada — o original passou do limite de envio';
    case 'NAVEGADOR':
      return 'imagem que estava guardada no navegador — o PDF original não subiu';
  }
}

/**
 * Os bytes de um `data:` em base64 — a imagem renderizada virando arquivo para subir.
 *
 * `atob` basta: isto só roda no navegador, e o `data:` foi o próprio `canvas` que escreveu.
 */
export function bytesDoDataUrl(dataUrl: string): Uint8Array {
  const virgula = dataUrl.indexOf(',');
  const binario = atob(dataUrl.slice(virgula + 1));
  const bytes = new Uint8Array(binario.length);
  for (let indice = 0; indice < binario.length; indice += 1) {
    bytes[indice] = binario.charCodeAt(indice);
  }
  return bytes;
}
